import React, { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Settings as SettingsIcon, Globe, Volume2, VolumeX, Info, Type, Music, Palette, Loader2, BookOpen, Headphones, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useAuth } from "@/contexts/AuthContext";
import { t, getStoredLang, setStoredLang } from "@/utils/i18n";
import { toast } from "sonner";
import axios from "axios";
import soundManager from "@/utils/SoundManager";
import ambientMusic from "@/utils/AmbientMusic";
import { resetOnboarding } from "@/components/game/OnboardingTour";
import MissingImagesPanel from "@/components/game/MissingImagesPanel";
import PushNotificationSettings from "@/components/game/PushNotificationSettings";
import * as storyTTS from "@/utils/storyTTS";
import PublishStoryPanel from "@/components/game/PublishStoryPanel";

const IMAGE_STYLES = [
  { id: "anime", label_en: "Anime", label_it: "Anime", emoji: "🎌" },
  { id: "realistic", label_en: "Realistic", label_it: "Realistico", emoji: "🖼️" },
  { id: "oil_painting", label_en: "Oil Painting", label_it: "Pittura ad Olio", emoji: "🎨" },
  { id: "pixel_art", label_en: "Pixel Art", label_it: "Pixel Art", emoji: "👾" },
  { id: "ghibli", label_en: "Studio Ghibli", label_it: "Studio Ghibli", emoji: "🌿" },
  { id: "dark_fantasy", label_en: "Dark Fantasy", label_it: "Dark Fantasy", emoji: "🌑" },
];

const LANGS = [
  { id: "it", name: "Italiano" }, { id: "en", name: "English" },
  { id: "es", name: "Espanol" }, { id: "fr", name: "Francais" },
  { id: "de", name: "Deutsch" }, { id: "pt", name: "Portugues" },
];

function getStoredFont() {
  return localStorage.getItem('ac_story_font') || 'cursive';
}

function applyFont(font) {
  if (typeof window !== 'undefined') {
    const fontName = font === 'classic' ? "'EB Garamond', 'Georgia', serif" : "'Dancing Script', cursive";
    document.documentElement.style.setProperty('--story-font', fontName);
  }
}

function setStoredFont(font) {
  localStorage.setItem('ac_story_font', font);
  document.documentElement.setAttribute('data-story-font', font);
  applyFont(font);
}

// Apply on load
if (typeof window !== 'undefined') {
  const saved = getStoredFont();
  document.documentElement.setAttribute('data-story-font', saved);
  applyFont(saved);
}

export default function SettingsPanel({ onLangChange, gameMode = false, gameLang, gameId, currentImageStyle, onImageStyleChange, characterAppearance, onAppearanceChange }) {
  const [uiLang, setUiLang] = useState(getStoredLang());
  const [soundOn, setSoundOn] = useState(soundManager.enabled);
  const [musicOn, setMusicOn] = useState(ambientMusic.enabled);
  const [musicVol, setMusicVol] = useState(ambientMusic.volume);
  const [sfxVol, setSfxVol] = useState(soundManager.volume);
  const [storyFont, setStoryFont] = useState(getStoredFont());
  const [savingStyle, setSavingStyle] = useState(false);
  const [savingAppearance, setSavingAppearance] = useState(false);
  const [appearance, setAppearance] = useState("");
  const [voiceType, setVoiceType] = useState(storyTTS.getStoredVoiceType());
  const [voiceSummary, setVoiceSummary] = useState({ total: 0, female: 0, male: 0, neutro: 0 });
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (characterAppearance) {
      setAppearance(characterAppearance);
    }
  }, [characterAppearance]);

  useEffect(() => {
    let mounted = true;
    storyTTS.ensureVoicesLoaded().then(() => {
      if (!mounted) return;
      setVoiceSummary(storyTTS.listVoicesSummary(gameLang || uiLang));
    });
    return () => { mounted = false; };
  }, [gameLang, uiLang]);

  const handlePreviewVoice = async (type) => {
    if (previewing) return;
    setPreviewing(true);
    try {
      const lang = gameLang || uiLang;
      const isIt = (lang || "en").startsWith("it");
      const sample = isIt
        ? "La pergamena si apre. Una nuova avventura ti attende, viaggiatore."
        : "The scroll unfolds. A new adventure awaits you, traveler.";
      await storyTTS.speak(sample, { lang, type, rate: 0.95 });
    } catch (e) {
      toast.error("TTS non disponibile su questo dispositivo");
    } finally {
      setPreviewing(false);
    }
  };
  const { token, API } = useAuth();
  const lang = uiLang;

  useEffect(() => {
    const saved = getStoredFont();
    document.documentElement.setAttribute('data-story-font', saved);
    applyFont(saved);
  }, []);

  const handleUiLangChange = (newLang) => {
    setUiLang(newLang);
    setStoredLang(newLang);
    if (onLangChange) onLangChange(newLang);
  };

  const handleSoundToggle = (checked) => {
    if (checked) { soundManager.enable(); } else { soundManager.disable(); }
    setSoundOn(checked);
  };

  const handleFontChange = (font) => {
    setStoryFont(font);
    setStoredFont(font);
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          data-testid="settings-button"
          variant="ghost"
          size="sm"
          className="text-[#94a3b8] hover:text-[#cfb53b] hover:bg-[#cfb53b]/10"
        >
          <SettingsIcon size={18} />
          <span className="hidden sm:inline ml-2">{t(lang, 'settings')}</span>
        </Button>
      </SheetTrigger>
      <SheetContent
        className="bg-[#1a1a1d] border-l border-[#cfb53b]/20 w-[320px] sm:w-[380px] overflow-y-auto max-h-[100dvh] pb-10"
        data-testid="settings-panel"
      >
        <SheetHeader>
          <SheetTitle className="font-heading text-[#cfb53b] text-xl flex items-center gap-2">
            <SettingsIcon size={20} />
            {t(lang, 'settings')}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-8 space-y-7">
          {/* UI Language */}
          <div className="space-y-3">
            <Label className="text-[#cfb53b] font-heading text-sm flex items-center gap-2">
              <Globe size={14} />
              {t(lang, 'ui_language')}
            </Label>
            <Select value={uiLang} onValueChange={handleUiLangChange}>
              <SelectTrigger className="bg-[#0a0a0c] border-[#cfb53b]/30 text-[#e2e8f0]" data-testid="settings-ui-lang">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a1d] border-[#cfb53b]/30">
                {LANGS.map(l => (
                  <SelectItem key={l.id} value={l.id} className="text-[#e2e8f0] focus:bg-[#cfb53b]/10 focus:text-[#cfb53b]">
                    {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[#64748b]">{t(lang, 'ui_language_hint')}</p>
          </div>

          {/* Game Story Language (only in game mode) */}
          {gameMode && (
            <div className="space-y-3">
              <Label className="text-[#cfb53b] font-heading text-sm flex items-center gap-2">
                <Globe size={14} />
                {t(lang, 'story_language')}
              </Label>
              <div className="bg-[#0a0a0c] border border-[#cfb53b]/30 rounded-md px-3 py-2.5 text-sm text-[#94a3b8]">
                {LANGS.find(l => l.id === gameLang)?.name || gameLang}
              </div>
              <p className="text-xs text-[#64748b] flex items-center gap-1">
                <Info size={10} />
                {t(lang, 'story_lang_locked')}
              </p>
            </div>
          )}

          {/* Game Image Style (only in game mode) */}
          {gameMode && gameId && (
            <div className="space-y-3">
              <Label className="text-[#cfb53b] font-heading text-sm flex items-center gap-2">
                <Palette size={14} />
                {lang.startsWith("it") ? "Stile Immagini" : "Image Style"}
              </Label>
              <div className="grid grid-cols-3 gap-1.5">
                {IMAGE_STYLES.map(s => {
                  const selected = (currentImageStyle || "anime") === s.id;
                  return (
                    <button
                      key={s.id}
                      data-testid={`settings-style-${s.id}`}
                      onClick={async () => {
                        if (savingStyle || selected) return;
                        setSavingStyle(true);
                        try {
                          await axios.patch(
                            `${API}/game/${gameId}/image-style`,
                            { image_style: s.id },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          toast.success(lang.startsWith("it") ? `Stile: ${s.label_it}` : `Style: ${s.label_en}`);
                          onImageStyleChange?.(s.id);
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Save failed");
                        } finally {
                          setSavingStyle(false);
                        }
                      }}
                      disabled={savingStyle}
                      className={`rounded-md border-2 p-1.5 text-center transition-all ${
                        selected
                          ? "border-[#cfb53b] bg-[#cfb53b]/10"
                          : "border-[#2d3748] bg-[#0a0a0c] hover:border-[#cfb53b]/40"
                      }`}
                    >
                      <div className="text-xl mb-0.5">{s.emoji}</div>
                      <p className="text-[9px] text-[#e2e8f0] leading-tight">
                        {lang.startsWith("it") ? s.label_it : s.label_en}
                      </p>
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#64748b] flex items-center gap-1">
                <Info size={10} />
                {lang.startsWith("it")
                  ? "Si applica alle nuove immagini generate. Le vecchie restano invariate."
                  : "Applies to newly generated images. Existing ones are unchanged."}
              </p>
            </div>
          )}

          {/* Protagonist Appearance (only in game mode) */}
          {gameMode && gameId && (
            <div className="space-y-3">
              <Label className="text-[#cfb53b] font-heading text-sm flex items-center gap-2">
                <User size={14} />
                {lang.startsWith("it") ? "Aspetto Protagonista" : "Protagonist Appearance"}
              </Label>
              <textarea
                data-testid="settings-appearance-input"
                value={appearance}
                onChange={(e) => setAppearance(e.target.value)}
                rows={3}
                placeholder={
                  lang.startsWith("it")
                    ? "Descrivi l'aspetto fisico del tuo personaggio (capelli, vestiti, dettagli)..."
                    : "Describe your character's physical appearance (hair, clothes, details)..."
                }
                className="w-full bg-[#0a0a0c] border border-[#cfb53b]/30 rounded-md px-3 py-2 text-sm text-[#cbd5e1] placeholder:text-[#64748b] focus:border-[#cfb53b] focus:ring-1 focus:ring-[#cfb53b] outline-none resize-none font-sans"
              />
              <Button
                data-testid="settings-appearance-save"
                onClick={async () => {
                  if (savingAppearance) return;
                  if (!appearance.trim()) {
                    toast.error(lang.startsWith("it") ? "L'aspetto non può essere vuoto" : "Appearance cannot be empty");
                    return;
                  }
                  setSavingAppearance(true);
                  try {
                    await axios.patch(
                      `${API}/game/${gameId}/appearance`,
                      { appearance: appearance.trim() },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    toast.success(lang.startsWith("it") ? "Aspetto aggiornato con successo" : "Appearance updated successfully");
                    onAppearanceChange?.(appearance.trim());
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Save failed");
                  } finally {
                    setSavingAppearance(false);
                  }
                }}
                disabled={savingAppearance || appearance.trim() === (characterAppearance || "").trim()}
                className="w-full bg-[#cfb53b] hover:bg-[#d4af37] text-black font-heading font-semibold text-xs py-1.5 h-auto"
              >
                {savingAppearance ? (
                  <span className="flex items-center gap-1.5 justify-center">
                    <Loader2 size={12} className="animate-spin" />
                    {lang.startsWith("it") ? "Salvataggio..." : "Saving..."}
                  </span>
                ) : (
                  lang.startsWith("it") ? "Salva Aspetto" : "Save Appearance"
                )}
              </Button>
              <p className="text-[10px] text-[#64748b] flex items-center gap-1 leading-snug">
                <Info size={10} className="shrink-0" />
                {lang.startsWith("it")
                  ? "Definisce i dettagli visivi usati dall'IA per disegnare il tuo personaggio nei prossimi pannelli."
                  : "Defines the visual details used by the AI to draw your character in upcoming panels."}
              </p>
            </div>
          )}

          {/* Hall of Stories — opt-in publishing (only in game mode) */}
          {gameMode && gameId && (
            <div className="pt-2 border-t border-[#cfb53b]/10">
              <PublishStoryPanel gameId={gameId} lang={lang} />
            </div>
          )}

          {/* Story Font */}
          <div className="space-y-3">
            <Label className="text-[#cfb53b] font-heading text-sm flex items-center gap-2">
              <Type size={14} />
              {t(lang, 'story_font')}
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="font-cursive"
                onClick={() => handleFontChange('cursive')}
                className={`rounded-lg border-2 p-3 text-center transition-all ${
                  storyFont === 'cursive'
                    ? 'border-[#cfb53b] bg-[#cfb53b]/10'
                    : 'border-[#2d3748] bg-[#0a0a0c] hover:border-[#cfb53b]/40'
                }`}
              >
                <span style={{ fontFamily: "'Dancing Script', cursive", fontSize: '18px' }} className="text-[#e2e8f0] block mb-1">
                  {t(lang, 'font_cursive')}
                </span>
                <span className="text-[10px] text-[#64748b]">{t(lang, 'font_cursive_desc')}</span>
              </button>
              <button
                data-testid="font-classic"
                onClick={() => handleFontChange('classic')}
                className={`rounded-lg border-2 p-3 text-center transition-all ${
                  storyFont === 'classic'
                    ? 'border-[#cfb53b] bg-[#cfb53b]/10'
                    : 'border-[#2d3748] bg-[#0a0a0c] hover:border-[#cfb53b]/40'
                }`}
              >
                <span style={{ fontFamily: "'EB Garamond', Georgia, serif", fontSize: '18px' }} className="text-[#e2e8f0] block mb-1">
                  {t(lang, 'font_classic')}
                </span>
                <span className="text-[10px] text-[#64748b]">{t(lang, 'font_classic_desc')}</span>
              </button>
            </div>
            {/* Preview */}
            <div className="bg-[#0a0a0c] border border-[#2d3748] rounded-lg p-3">
              <p className="text-[10px] text-[#64748b] font-heading mb-1.5">{t(lang, 'font_preview')}</p>
              <p
                className="text-[#2a2a2a] leading-relaxed text-base rounded px-3 py-2"
                style={{
                  fontFamily: storyFont === 'classic' ? "'EB Garamond', Georgia, serif" : "'Dancing Script', cursive",
                  backgroundColor: '#d4c5a9',
                  fontSize: storyFont === 'classic' ? '16px' : '17px',
                }}
              >
                {t(lang, 'font_preview_text')}
              </p>
            </div>
          </div>

          {/* Background Music */}
          <div className="space-y-3">
            <Label className="text-[#cfb53b] font-heading text-sm flex items-center gap-2">
              <Music size={14} />
              {t(lang, 'background_music')}
            </Label>
            <div className="bg-[#0a0a0c] border border-[#2d3748] rounded-lg px-4 py-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#e2e8f0]">
                  {musicOn ? t(lang, 'sound_on') : t(lang, 'sound_off')}
                </span>
                <Switch
                  data-testid="settings-music-toggle"
                  checked={musicOn}
                  onCheckedChange={(checked) => {
                    setMusicOn(checked);
                    ambientMusic.setEnabled(checked);
                    if (checked) ambientMusic.play(ambientMusic.currentMood || 'menu');
                  }}
                  className="data-[state=checked]:bg-[#cfb53b]"
                />
              </div>
              {musicOn && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#94a3b8]">{t(lang, 'volume')}</span>
                    <span className="text-xs text-[#cfb53b] font-heading">{Math.round(musicVol * 100)}%</span>
                  </div>
                  <Slider
                    data-testid="settings-music-volume"
                    value={[musicVol * 100]}
                    onValueChange={([v]) => { const vol = v / 100; setMusicVol(vol); ambientMusic.setVolume(vol); }}
                    max={100}
                    step={5}
                    className="w-full"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Sound Effects */}
          <div className="space-y-3">
            <Label className="text-[#cfb53b] font-heading text-sm flex items-center gap-2">
              {soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
              {t(lang, 'sound_effects')}
            </Label>
            <div className="bg-[#0a0a0c] border border-[#2d3748] rounded-lg px-4 py-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#e2e8f0]">
                  {soundOn ? t(lang, 'sound_on') : t(lang, 'sound_off')}
                </span>
                <Switch
                  data-testid="settings-sound-toggle"
                  checked={soundOn}
                  onCheckedChange={handleSoundToggle}
                  className="data-[state=checked]:bg-[#cfb53b]"
                />
              </div>
              {soundOn && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#94a3b8]">{t(lang, 'volume')}</span>
                    <span className="text-xs text-[#cfb53b] font-heading">{Math.round(sfxVol * 100)}%</span>
                  </div>
                  <Slider
                    data-testid="settings-sfx-volume"
                    value={[sfxVol * 100]}
                    onValueChange={([v]) => { const vol = v / 100; setSfxVol(vol); soundManager.setVolume(vol); }}
                    max={100}
                    step={5}
                    className="w-full"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Missing images: deferred regeneration */}
          {gameMode && <MissingImagesPanel variant="panel" />}

          {/* Push notifications */}
          <PushNotificationSettings variant="panel" lang={lang} />

          {/* Story TTS — narrator voice picker */}
          {storyTTS.isSupported() && (
            <div className="space-y-2">
              <Label className="text-[#cfb53b] font-heading text-sm flex items-center gap-2">
                <Headphones size={14} />
                {lang.startsWith("it") ? "Voce narratore" : "Narrator voice"}
              </Label>
              <p className="text-[10px] text-[#94a3b8]/80 leading-snug -mt-1">
                {lang.startsWith("it")
                  ? `${voiceSummary.total} voci disponibili (${voiceSummary.female}F · ${voiceSummary.male}M · ${voiceSummary.neutro}N)`
                  : `${voiceSummary.total} voices available (${voiceSummary.female}F · ${voiceSummary.male}M · ${voiceSummary.neutro}N)`}
              </p>
              <Select
                value={voiceType}
                onValueChange={(v) => { setVoiceType(v); storyTTS.setStoredVoiceType(v); }}
              >
                <SelectTrigger className="bg-[#0a0a0c] border-[#cfb53b]/30 text-[#cbd5e1]" data-testid="tts-voice-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1d] border-[#cfb53b]/30 text-[#cbd5e1]">
                  <SelectItem value="auto">{lang.startsWith("it") ? "Automatico (sistema)" : "Auto (system)"}</SelectItem>
                  <SelectItem value="female">{lang.startsWith("it") ? "Femminile" : "Female"}</SelectItem>
                  <SelectItem value="male">{lang.startsWith("it") ? "Maschile" : "Male"}</SelectItem>
                  <SelectItem value="neutro">{lang.startsWith("it") ? "Neutro" : "Neutral"}</SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={() => handlePreviewVoice(voiceType)}
                disabled={previewing}
                variant="outline"
                size="sm"
                data-testid="tts-preview-btn"
                className="w-full border-[#cfb53b]/30 text-[#cfb53b] hover:bg-[#cfb53b]/10"
              >
                {previewing
                  ? <><Loader2 size={14} className="mr-2 animate-spin" /> {lang.startsWith("it") ? "Riproduzione..." : "Playing..."}</>
                  : <><Headphones size={14} className="mr-2" /> {lang.startsWith("it") ? "Anteprima voce" : "Preview voice"}</>}
              </Button>
            </div>
          )}

          {/* Replay tutorial */}
          <div className="space-y-2">
            <Label className="text-[#cfb53b] font-heading text-sm flex items-center gap-2">
              <BookOpen size={14} />
              {lang.startsWith("it") ? "Tutorial" : "Tutorial"}
            </Label>
            <SheetClose asChild>
              <Button
                data-testid="replay-tutorial-btn"
                onClick={() => {
                  resetOnboarding();
                  // Let the Sheet finish its close animation (~300ms) before
                  // dispatching the event — otherwise the Sheet overlay covers
                  // the newly-opened tour tooltip on mobile.
                  setTimeout(() => {
                    window.dispatchEvent(new Event("ac:show-onboarding"));
                  }, 350);
                }}
                variant="outline"
                className="w-full border-[#cfb53b]/30 text-[#cfb53b] hover:bg-[#cfb53b]/10 hover:text-[#cfb53b] hover:border-[#cfb53b]/60"
              >
                <BookOpen size={14} className="mr-2" />
                {lang.startsWith("it") ? "Rivedi tutorial" : "Replay tutorial"}
              </Button>
            </SheetClose>
          </div>

          {/* Info */}
          <div className="pt-4 border-t border-[#2d3748]">
            <p className="text-xs text-[#64748b] text-center">Arcane Chronicles v1.4</p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
